import type { ReactNode } from "react";
import { StatusDropdown, type StatusOption } from "admin/components/StatusDropdown";
import { SEVERITY_INDICATOR_CONFIG } from "admin/types/constants/diagnostics";
import { REVIEW_STATUS_OPTIONS } from "admin/types/constants/review-status";
import type { ReviewStatus } from "admin/types/interfaces/conversation";
import type { DiagnosticSeverity } from "admin/types/interfaces/diagnostics";
import { scoreColor } from "admin/utils/scoring";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

// Solid fills rather than the per-turn indicator's `bg-*-100` tints, which are
// too faint at this bar height. N.B. the hues stay inside the set
// `src/index.css` inverts under `.dark`, so no `dark:` variants are needed.
const SEVERITY_BAR_CLASS = {
  high: "bg-red-500",
  medium: "bg-orange-500",
  low: "bg-green-500",
} as const satisfies Record<DiagnosticSeverity, string>;

// Most severe on top, so the strip reads as a triage priority instead of
// reshuffling itself whenever the counts shift.
const SEVERITY_MIX_ORDER: readonly DiagnosticSeverity[] = ["high", "medium", "low"];

const UNSCORED_LABEL = SEVERITY_INDICATOR_CONFIG.unscored.label;

// `StatusDropdown` takes `badgeClass`; the shared constant calls the same thing
// `color` and has four other consumers, so map rather than rename it there.
const REVIEW_STATUS_DROPDOWN_OPTIONS: readonly StatusOption<ReviewStatus>[] =
  REVIEW_STATUS_OPTIONS.map(({ value, label, color }) => ({ value, label, badgeClass: color }));

export interface SessionDiagnosticsBandProps {
  /** Mean rubric score over the session's scored turns; `null` when none are scored. */
  sessionScore: number | null;
  severityCounts: Record<DiagnosticSeverity, number>;
  /** Assistant turns the diagnostics run has not covered yet. */
  unscoredCount: number;
  /** The conversation's current review status; `undefined` reads as unreviewed. */
  reviewStatus: ReviewStatus | undefined;
  onReviewStatusChange: (next: ReviewStatus) => Promise<void>;
}

/** Session-level rollup strip sitting between the app header and the transcript. */
export const SessionDiagnosticsBand = ({
  sessionScore,
  severityCounts,
  unscoredCount,
  reviewStatus,
  onReviewStatusChange,
}: SessionDiagnosticsBandProps) => (
  // N.B. every stat is `shrink-0`, so nothing here compresses and the last one is
  // clipped rather than squeezed once the stats outgrow the window. `flex-wrap`
  // is what keeps the review-status dropdown reachable at narrow widths.
  // A stat is its uppercase label stacked over its value row, so `items-center`
  // centres a bare `h-8` child against the whole taller stack; the dividers are
  // `self-end` to sit on the value rows with the columns they separate.
  <div
    role="region"
    aria-label="Session diagnostics"
    className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 border-b border-border bg-muted/40 px-6 py-2"
  >
    <BandStat label="Session score">
      <span
        className={cn("text-lg leading-tight font-semibold tabular-nums", scoreColor(sessionScore))}
      >
        {sessionScore == null ? "N/A" : sessionScore.toFixed(2)}
        {sessionScore != null && (
          <span className="text-xs font-medium text-muted-foreground">{" / 5"}</span>
        )}
      </span>
    </BandStat>
    <BandDivider />
    <BandStat label="Severity mix">
      <SeverityMix severityCounts={severityCounts} unscoredCount={unscoredCount} />
    </BandStat>
    <BandDivider />
    <BandStat label="Review status">
      <StatusDropdown
        value={reviewStatus}
        options={REVIEW_STATUS_DROPDOWN_OPTIONS}
        onChange={onReviewStatusChange}
        ariaLabel="Update session review status"
        errorMessage="Failed to update review status"
      />
    </BandStat>
  </div>
);

const BandDivider = () => <div className="h-8 w-px shrink-0 self-end bg-border" />;

const BandStat = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex shrink-0 flex-col gap-1">
    <span className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
      {label}
    </span>
    {/* Fixed value-row height so every stat's label sits on one line, whatever
        the value renders as. `h-8` because the band is `items-center`: the
        tallest value in the row is the review-status trigger, and any stat
        shorter than it would centre against it and drop its label off the
        shared baseline. */}
    <div className="flex h-8 items-center">{children}</div>
  </div>
);

interface SeverityMixProps {
  severityCounts: Record<DiagnosticSeverity, number>;
  unscoredCount: number;
}

/**
 * Severity shares as stacked bars over the scored turns, with the same
 * breakdown available as text so it is not hover-only information.
 */
const SeverityMix = ({ severityCounts, unscoredCount }: SeverityMixProps) => {
  const scoredTotal = SEVERITY_MIX_ORDER.reduce((total, key) => total + severityCounts[key], 0);
  const bars = SEVERITY_MIX_ORDER.filter((key) => severityCounts[key] > 0).map((severity) => ({
    severity,
    count: severityCounts[severity],
    label: SEVERITY_INDICATOR_CONFIG[severity].label,
    percent: Math.round((severityCounts[severity] / scoredTotal) * 100),
  }));

  if (bars.length === 0 && unscoredCount === 0) {
    return <span className="text-xs text-muted-foreground">No turns</span>;
  }

  const breakdown = [
    ...bars.map(({ label, count, percent }) => `${label} ${formatTurnCount(count)} (${percent}%)`),
    ...(unscoredCount > 0 ? [`${UNSCORED_LABEL} ${formatTurnCount(unscoredCount)}`] : []),
  ].join(", ");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          aria-label={`Severity mix: ${breakdown}`}
          tabIndex={0}
          className="flex w-[104px] flex-col gap-0.5 rounded-[2px] focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {bars.length > 0 ? (
            bars.map(({ severity, percent }) => (
              <span
                key={severity}
                style={{ width: `${percent}%` }}
                className={cn("h-1.5 min-w-1.5 rounded-[2px]", SEVERITY_BAR_CLASS[severity])}
              />
            ))
          ) : (
            <span className="h-1.5 w-full rounded-[2px] bg-muted-foreground/30" />
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" sideOffset={6} hideArrow className="px-2.5 py-2">
        <div className="flex flex-col gap-1.5 text-xs leading-none">
          {bars.map(({ severity, label, count, percent }) => (
            <div key={severity} className="flex items-center gap-2">
              <span
                className={cn("size-1.5 shrink-0 rounded-[2px]", SEVERITY_BAR_CLASS[severity])}
              />
              <span>{`${label} · ${formatTurnCount(count)} (${percent}%)`}</span>
            </div>
          ))}
          {unscoredCount > 0 && (
            <div className="text-background/70">
              {`${UNSCORED_LABEL} · ${formatTurnCount(unscoredCount)}`}
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

function formatTurnCount(count: number): string {
  return `${count} ${count === 1 ? "turn" : "turns"}`;
}
